import { useEffect, useMemo, useState } from 'react';
import { demeterMarketIndicatorsApi } from '../../../../Apis/Apis';
import { DemeterUserStoreType, MarketIndicatorModel } from '../../../../Generated/Raven-Demeter';
import useCacheThenApi from '../../../Apis/Hooks/useCacheThenApiHook';
import useUserStoreApi from '../../../Apis/Hooks/useUserStoreApiHook';
import IndicatorsManagementButton from '../../../Components/Form/Buttons/IndicatorsManagementButton/IndicatorsManagementButton';
import CardSkeleton from '../../../Components/Layout/CardSkeleton';
import ManageMarketIndicatorsModal from '../../../Components/Modals/ProductsModal/ManageCommodityIndicatorsModal';
import CacheKeys from '../../../Services/Cache/CacheKeys';
import useLanguage from '../../../Services/Language/useLanguageHook';
import MarketIndicatorGuageChart from '../../MarketIndicators/MarketIndicatorGuageChart';
import styles from './CustomDefaultDashboardComponents.module.scss';

const defaultChartDimensions = { chartSize: 140, dialRadius: 60 };

interface ICustomDashboardMarketIndicatorsProps {
    useDynamicHeight?: boolean;
}

const CustomDashboardMarketIndicators: React.FC<ICustomDashboardMarketIndicatorsProps> = ({ useDynamicHeight }) => {
    // Application hooks.
    const [translations, translate] = useLanguage();
    const [userMarketIndicatorsResponse, updateUserMarketIndicators] = useUserStoreApi(DemeterUserStoreType.UserMarketIndicators);
    const [manageCommodityIndicatorsModalOpen, setManageCommodityIndicatorsModalOpen] = useState(false);
    const [userMarketIndicators, setUserMarketIndicators] = useState<string[] | undefined>(undefined);

    // Text hooks.
    const marketIndicatorTitleString = useMemo(() => translations.dashboard.headings.marketIndicatorsHeader, [translations]);

    // Api hooks.
    const [isLoading, , listMarketIndicatorsResponse] = useCacheThenApi(CacheKeys.ListMarketIndicatorsCurrent, () =>
        demeterMarketIndicatorsApi.listMarketIndicatorsCurrent(true),
    );

    const marketIndicators = useMemo(() => listMarketIndicatorsResponse?.rows ?? [], [listMarketIndicatorsResponse]);

    const selectedMarketIndicators = useMemo(() => {
        if (!marketIndicators || !userMarketIndicators) {
            return null;
        }

        const selected: MarketIndicatorModel[] = [];
        userMarketIndicators.forEach((val) => {
            const match = marketIndicators.find((x) => x.marketIndicatorGuid === val);
            if (match) {
                selected.push(match);
            }
        });

        return selected;
    }, [marketIndicators, userMarketIndicators]);

    useEffect(() => setUserMarketIndicators(userMarketIndicatorsResponse?.userStore?.value?.userMarketIndicatorGuids ?? []), [userMarketIndicatorsResponse]);

    const handleUserMarketIndicatorsUpdate = async (indicators: MarketIndicatorModel[]) => {
        const updatedUserMarketIndicators = indicators?.map((x) => x.marketIndicatorGuid);
        setUserMarketIndicators(updatedUserMarketIndicators);
        updateUserMarketIndicators({
            userMarketIndicatorGuids: updatedUserMarketIndicators,
        });
    };

    const showLoadingSpinner = isLoading || !selectedMarketIndicators;

    const containerClassName = useDynamicHeight ? styles.commodity_indicators_container_dynamic_height : styles.commodity_indicators_container;
    const chartsClassName = useDynamicHeight ? styles.commodity_indicators_guage_charts_dynamic_height : styles.commodity_indicators_guage_charts;

    return showLoadingSpinner ? (
        <CardSkeleton />
    ) : (
        <div className={containerClassName}>
            <div data-testid="DashboardMarketIndicators">
                <div className={chartsClassName}>
                    {selectedMarketIndicators?.map((x) => (
                        <div className={styles.commodity_indicators_guage_chart} key={x.marketIndicatorGuid}>
                            <MarketIndicatorGuageChart
                                title={translate(x.displayName ?? '')}
                                chartDimensions={defaultChartDimensions}
                                marketIndicator={x}
                                isSmallGuage
                            />
                        </div>
                    ))}
                    <IndicatorsManagementButton
                        handleClick={() => setManageCommodityIndicatorsModalOpen(true)}
                        tooltipText={translations.dashboard.indicators.addOrRemoveCommodityIndicators}
                        track={{ eventCategory: 'Dashboard', eventAction: 'OpenManageMarketIndicatorsModalClicked' }}
                    />
                    <ManageMarketIndicatorsModal
                        open={manageCommodityIndicatorsModalOpen}
                        title={marketIndicatorTitleString}
                        selected={selectedMarketIndicators}
                        indicators={marketIndicators}
                        handleClose={() => setManageCommodityIndicatorsModalOpen(false)}
                        handleChange={handleUserMarketIndicatorsUpdate}
                    />
                </div>
            </div>
        </div>
    );
};

export default CustomDashboardMarketIndicators;
